/**
 * useGraphAPI - React hook for interacting with Microsoft Graph API
 */

import { useState, useCallback, useEffect } from 'react';
import GraphClientService, {
  GraphUser,
  GraphGroup,
} from '../services/graphClient';

interface UseGraphAPIReturn {
  loading: boolean;
  error: string | null;
  currentUser: GraphUser | null;
  currentUserPhoto: string | null;
  currentUserManager: GraphUser | null;
  currentUserGroups: GraphGroup[] | null;

  fetchCurrentUserProfile: () => Promise<GraphUser | null>;
  fetchCurrentUserPhoto: () => Promise<string | null>;
  fetchCurrentUserManager: () => Promise<GraphUser | null>;
  fetchCurrentUserGroups: () => Promise<GraphGroup[] | null>;
  clearError: () => void;
}

export const useGraphAPI = (autoLoadProfile: boolean = false): UseGraphAPIReturn => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<GraphUser | null>(null);
  const [currentUserPhoto, setCurrentUserPhoto] = useState<string | null>(null);
  const [currentUserManager, setCurrentUserManager] = useState<GraphUser | null>(null);
  const [currentUserGroups, setCurrentUserGroups] = useState<GraphGroup[] | null>(null);

  const graphService = GraphClientService.getInstance();
  
  // Check if Graph API is enabled via environment variable
  const isGraphEnabled = process.env.REACT_APP_ENABLE_GRAPH_API === 'true';

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const fetchCurrentUserProfile = useCallback(async (): Promise<GraphUser | null> => {
    if (!isGraphEnabled) {
      console.info('Graph API is disabled. Skipping profile fetch.');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Request specific fields including location data for HR policy personalization
      const profileFields = [
        'id', 'displayName', 'mail', 'userPrincipalName',
        'givenName', 'surname', 'jobTitle', 'department',
        'officeLocation', 'city', 'state', 'country',
        'mobilePhone', 'businessPhones'
      ];
      const profile = await graphService.getCurrentUserProfile(profileFields);
      setCurrentUser(profile);
      return profile;
    } catch (err: any) {
      // Check if error is because Graph is disabled
      if (err.statusCode === 503 || err.message?.includes('disabled')) {
        console.info('Graph API is disabled on server');
        return null;
      }
      
      const errorMessage = err.message || 'Failed to fetch user profile';
      setError(errorMessage);
      console.error('Error fetching current user profile:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [graphService, isGraphEnabled]);

  const fetchCurrentUserPhoto = useCallback(async (): Promise<string | null> => {
    if (!isGraphEnabled) {
      console.info('Graph API is disabled. Skipping photo fetch.');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);

      const photoUrl = await graphService.getCurrentUserPhoto();
      setCurrentUserPhoto(photoUrl);
      return photoUrl;
    } catch (err: any) {
      // Check if error is because Graph is disabled
      if (err.statusCode === 503 || err.message?.includes('disabled')) {
        console.info('Graph API is disabled on server');
        return null;
      }
      
      const errorMessage = err.message || 'Failed to fetch user photo';
      setError(errorMessage);
      console.error('Error fetching current user photo:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [graphService, isGraphEnabled]);

  const fetchCurrentUserManager = useCallback(async (): Promise<GraphUser | null> => {
    if (!isGraphEnabled) {
      console.info('Graph API is disabled. Skipping manager fetch.');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);

      const manager = await graphService.getCurrentUserManager();
      setCurrentUserManager(manager);
      return manager;
    } catch (err: any) {
      // Check if error is because Graph is disabled
      if (err.statusCode === 503 || err.message?.includes('disabled')) {
        console.info('Graph API is disabled on server');
        return null;
      }
      
      const errorMessage = err.message || 'Failed to fetch user manager';
      setError(errorMessage);
      console.error('Error fetching current user manager:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [graphService, isGraphEnabled]);

  const fetchCurrentUserGroups = useCallback(async (): Promise<GraphGroup[] | null> => {
    if (!isGraphEnabled) {
      console.info('Graph API is disabled. Skipping groups fetch.');
      return null;
    }
    
    try {
      setLoading(true);
      setError(null);

      const groups = await graphService.getCurrentUserGroups();
      setCurrentUserGroups(groups);
      return groups;
    } catch (err: any) {
      // Check if error is because Graph is disabled
      if (err.statusCode === 503 || err.message?.includes('disabled')) {
        console.info('Graph API is disabled on server');
        return null;
      }
      
      const errorMessage = err.message || 'Failed to fetch user groups';
      setError(errorMessage);
      console.error('Error fetching current user groups:', err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [graphService, isGraphEnabled]);

  useEffect(() => {
    if (autoLoadProfile) {
      fetchCurrentUserProfile();
    }
  }, [autoLoadProfile, fetchCurrentUserProfile]);

  return {
    loading,
    error,
    currentUser,
    currentUserPhoto,
    currentUserManager,
    currentUserGroups,
    fetchCurrentUserProfile,
    fetchCurrentUserPhoto,
    fetchCurrentUserManager,
    fetchCurrentUserGroups,
    clearError,
  };
};

export default useGraphAPI;


